import React, { FC, useContext, useMemo } from 'react';
import qs from 'query-string';
import { useLocation } from 'react-router-dom';
import ChildIssuesIcon from '@atlaskit/icon/glyph/child-issues';
import { SloGroupCohortSummaryDTO, SloGroupDTO } from '@atlassiansox/phobos-client-ts';
import EmptyState from '@atlaskit/empty-state';

import {
  breakWord,
  extendWidth,
  flex,
  flexDirectionColumn,
  flexStart,
  spaceGapMinimum,
  spaceGapRelaxed,
  spaceTopCozy,
} from '@/styles';
import { convertCommaSeparatedToQueryParams } from '@/utils';
import { StageDisplayContext, StageDisplayType } from '@/controllers/stage-display-controller';

import { CohortMemberCohortTypeEnum } from '../../types';
import FilterBar from '../filter-bar';
import { stickyHeadStyles } from '../styles';
import { sloGroupSearchObjectType } from '../types';

import SLOCohortMimir from './slo-cohort-mimir';

type SLOPanelMimirProps = {
  data: SloGroupDTO;
};

const SLOPanelMimir: FC<SLOPanelMimirProps> = ({ data: sloGroupData }) => {
  const { publicId, name } = sloGroupData;

  const { stageDisplayType } = useContext(StageDisplayContext);
  const isFullScreen = stageDisplayType === StageDisplayType.FullScreen;

  const { search } = useLocation();
  const { hasBreachStatusOnly, hideDrafts, cohorts } = useMemo(() => {
    const parsedSearch = qs.parse(search);
    const { environments, shardNames, cloudIds, ...rest }: sloGroupSearchObjectType = parsedSearch;
    const cohortsMap = new Map<string, string>();
    cohortsMap.set('environment', convertCommaSeparatedToQueryParams(environments, 'environment'));
    cohortsMap.set('shardName', convertCommaSeparatedToQueryParams(shardNames, 'shardName'));
    cohortsMap.set('cloudId', convertCommaSeparatedToQueryParams(cloudIds, 'cloudId'));

    return {
      ...rest,
      cohorts: cohortsMap,
    };
  }, [search]);

  const sloGroupCohortSummaries: SloGroupCohortSummaryDTO[] =
    sloGroupData?.sloGroupSummary?.sloGroupCohortSummaries || [];
  const hasCohorts: boolean = sloGroupCohortSummaries.length > 0;
  if (!hasCohorts) {
    return (
      <EmptyState
        header=""
        description="No cohorts or SLOs found for this SLO group"
        imageUrl="/assets/not-found.svg"
      />
    );
  }

  const cohortTypesSorted = sloGroupCohortSummaries.map((c: SloGroupCohortSummaryDTO) => c.cohort);

  // Move 'GLOBAL' to the first position if it exists
  const globalIndex = cohortTypesSorted.findIndex(
    (type: string) => type === CohortMemberCohortTypeEnum.Global
  );
  if (globalIndex > 0) {
    cohortTypesSorted.splice(globalIndex, 1);
    cohortTypesSorted.unshift(CohortMemberCohortTypeEnum.Global);
  }

  return (
    <div css={extendWidth}>
      <div css={isFullScreen ? stickyHeadStyles : ''}>
        {isFullScreen && (
          <div css={[breakWord]}>
            <h4 css={[flexStart, spaceGapMinimum]}>
              <ChildIssuesIcon label="SLO Group Icon" size="small" />
              {name}
            </h4>
          </div>
        )}
        <FilterBar
          hideCohortType
          hideCohortFilters
          publicId={publicId}
          sloGroupCohortSummaries={sloGroupCohortSummaries}
          baseLabel=""
          cohortTypeSorted={cohortTypesSorted}
        />
      </div>
      <div css={[spaceTopCozy, flexDirectionColumn, flex, spaceGapRelaxed]}>
        {cohortTypesSorted.map((cohort: string) => {
          const sloGroupCohortSummary = sloGroupCohortSummaries.find(
            x => x.cohort.toUpperCase() === cohort.toUpperCase()
          );
          if (sloGroupCohortSummary && (!hideDrafts || !sloGroupCohortSummary.draft)) {
            return (
              <SLOCohortMimir
                key={`${publicId}:${sloGroupCohortSummary.cohort}`}
                hasBreachStatusOnly={hasBreachStatusOnly}
                sloGroupCohortSummary={sloGroupCohortSummary}
                publicId={publicId}
                cohorts={cohorts}
              />
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default SLOPanelMimir;
